/**
 * Project Profile 的**六维取值**与后端 content 之间的转换。
 *
 * 后端 `ProfileOut.content` 是完整 Profile（13 个键），其中六维的形态不一致：
 * - `product_form` / `interaction_mode` / `dramatic_genre`：`{ primary, secondary, … }`
 * - `subject_domain`：`{ era, setting, geography, … }`（向导里选的是 `era`）
 * - `audience_profile` / `platform_profile`：裸字符串
 *
 * 向导内部统一使用扁平的 `Record<维度, 值>`，避免每条分支各写一次解包逻辑。
 */

import type { Json } from "@/api/types";
import type { DimensionId, DimensionDef } from "@/lib/taxonomy";
import { TAXONOMY } from "@/lib/taxonomy";

export type Selections = Partial<Record<DimensionId, string>>;

const NESTED_DIMENSIONS: DimensionId[] = ["product_form", "interaction_mode", "dramatic_genre"];

function readDimension(content: Record<string, Json>, id: DimensionId): string | undefined {
  const raw = content[id];
  if (raw === null || raw === undefined) return undefined;
  if (typeof raw === "string") return raw;
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const record = raw as Record<string, Json>;
    const key = id === "subject_domain" ? "era" : "primary";
    const value = record[key];
    return typeof value === "string" ? value : undefined;
  }
  return undefined;
}

/** 后端 content → 向导选择。只取六维，其余字段原样保留在 content 里。 */
export function toSelections(content: Record<string, Json> | undefined): Selections {
  const result: Selections = {};
  if (!content) return result;
  for (const dimension of TAXONOMY) {
    const value = readDimension(content, dimension.id);
    if (value) result[dimension.id] = value;
  }
  return result;
}

/**
 * 向导选择 → 后端 content。
 * 以既有 content 为基底，只覆盖六维，避免丢失 `player_role`、`research_gates` 等扩展字段。
 */
export function toContent(
  selections: Selections,
  base: Record<string, Json> = {},
): Record<string, Json> {
  const content: Record<string, Json> = { ...base };

  for (const dimension of TAXONOMY) {
    const value = selections[dimension.id];
    if (!value) continue;

    if (dimension.id === "subject_domain") {
      const previous =
        typeof base.subject_domain === "object" &&
        base.subject_domain !== null &&
        !Array.isArray(base.subject_domain)
          ? (base.subject_domain as Record<string, Json>)
          : {};
      content.subject_domain = {
        ...previous,
        era: value,
        setting: previous.setting ?? DEFAULT_SETTING[value] ?? "待补充",
      };
      continue;
    }

    if (NESTED_DIMENSIONS.includes(dimension.id)) {
      const previous =
        typeof base[dimension.id] === "object" &&
        base[dimension.id] !== null &&
        !Array.isArray(base[dimension.id])
          ? (base[dimension.id] as Record<string, Json>)
          : {};
      content[dimension.id] = { ...previous, primary: value };
      continue;
    }

    content[dimension.id] = value;
  }

  return content;
}

/** 每个题材域的默认 setting，用于新建项目时给出可执行的初值。 */
const DEFAULT_SETTING: Record<string, string> = {
  song_dynasty_fiction: "清河县 · 武家与市集",
  republican_era: "上海租界 · 电讯科",
  contemporary_urban: "一线城市 · 写字楼与租住区",
  fantasy_otherworld: "架空城邦 · 待定",
};

export function dimensionByIndex(index: number): DimensionDef | undefined {
  return TAXONOMY.find((dimension) => dimension.index === index);
}
